import React from 'react';
import { t } from '../utils/i18n';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { TextField } from 'formik-material-ui';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Grid, Modal, Box, Typography, TextField as MUITextField} from '@material-ui/core';

// Context
import { useAuth } from "../context/auth";

// Services
import AuthService from "../services/auth.service";

// App components
import Alert from "./Alert";
import Link from "./Link";

const useStyles = makeStyles(theme => ({
  form: { marginTop: theme.spacing(2) },
  link: { textDecoration: 'none' },
  modal: {
    width: 500,
    maxWidth: 'calc(100vw - 40px)',
    maxHeight: 'calc(100vh - 40px)',
    overflowY: 'auto',
    background: '#fff',
    padding: 20,
    margin: 'auto',
    height: 'fit-content',
    top: 0,
    bottom: 0,
    position: 'absolute',
    left: 0,
    right: 0
  },
  qr: { display: 'block', margin: '15px 0', width: 200, height: 200 },
  secret: {
    display: 'inline-block',
    fontFamily: 'monospace',
    letterSpacing: 1,
    background: '#f5f5f5',
    padding: '4px 8px',
    wordBreak: 'break-all'
  }
}));

const SigninSchema = Yup.object().shape({
  email: Yup.string()
    .required(t('required')),
  password: Yup.string()
    .required(t('required'))
});

// The server tells us why a code was refused; anything unexpected falls back to
// the generic message.
const verificationErrors = {
  invalid_code: 'twofactor_invalid_code',
  code_already_used: 'twofactor_code_already_used',
  locked: 'twofactor_locked',
  not_enrolled: 'twofactor_not_enrolled',
  challenge_expired: 'twofactor_challenge_expired'
};

export default function SigninForm() {
  const { setUser } = useAuth();
  const classes = useStyles();
  const [error, setError] = React.useState(false);
  const [challenge, setChallenge] = React.useState(false);
  const [verificationError, setVerificationError] = React.useState(false);
  const [verificationCode, setVerificationCode] = React.useState('');
  const [verifying, setVerifying] = React.useState(false);

  const login = (values, { setSubmitting }) => {
    setError(false);
    AuthService.login(values).then((response) => {
      if (response && response.mfaRequired) {
        setVerificationCode('');
        setVerificationError(false);
        setSubmitting(false);
        return setChallenge(response);
      }
      return setUser(response);
    })
    .catch(() => {
      setError(true);
      setSubmitting(false);
    });
  };

  const closeChallenge = () => {
    setChallenge(false);
    setVerificationCode('');
    setVerificationError(false);
  };

  const verify2FA = () => {
    if (verifying || ! verificationCode) return;
    setVerifying(true);
    setVerificationError(false);
    AuthService.verify2FA({ mfaToken: challenge.mfaToken, code: verificationCode })
      .then((data) => {
        setVerifying(false);
        setChallenge(false);
        setUser(data);
      })
      .catch((requestError) => {
        setVerifying(false);
        const { reason } = (requestError.response && requestError.response.data) || {};
        setVerificationError(verificationErrors[reason] || 'twofactor_invalid_code');
      });
  };

  const enrollment = challenge && challenge.enrollment;

  return (
    <>
      <Formik
        initialValues={{ email: '', password: '' }}
        validationSchema={SigninSchema}
        onSubmit={login}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ submitForm, isSubmitting }) => (
          <Form className={classes.form}>
            {error && <Alert severity="error">Gegevens niet correct.</Alert>}
            <Grid container className={classes.container}>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="email"
                  style={{ marginBottom: 20 }}
                  type="email"
                  autoComplete="email"
                  variant="outlined"
                  label={t('email')}
                  onKeyUp={event => event.keyCode === 13 && submitForm()}
                  fullWidth />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  fullWidth
                  style={{ marginBottom: 20 }}
                  variant="outlined"
                  onKeyUp={event => event.keyCode === 13 && submitForm()}
                  name="password"
                  type="password"
                  label={t('password')}
                  autoComplete="current-password" />
              </Grid>
            </Grid>
            <br />
            <Button
              variant="contained"
              color="primary"
              style={{ backgroundColor: '#0df394', color: '#041e7a', marginRight: 20 }}
              size='large'
              disabled={isSubmitting}
              onClick={submitForm}
            >
              {t('login')} &gt;
            </Button>
            <Link className={classes.link} href="/reset-password">{t('forgot_password')}</Link>
          </Form>
        )}
      </Formik>
      <Modal
        open={!! challenge}
        onClose={closeChallenge}
        aria-labelledby="twofactor-modal-title"
        aria-describedby="twofactor-modal-description"
      >
        <Box className={classes.modal}>
          <Typography id="twofactor-modal-title" variant="h6" component="h2">
            {enrollment ? t('twofactor_setup_title') : t('twofactor_title')}
          </Typography>
          {verificationError && <Alert severity="error">{t(verificationError)}</Alert>}
          {enrollment ? (
            <>
              <Typography id="twofactor-modal-description" variant="body2" style={{ marginTop: 10 }}>
                {t('twofactor_setup_info')}
              </Typography>
              <img className={classes.qr} src={enrollment.qr} alt={t('twofactor_setup_title')} />
              <Typography variant="body2">
                {t('twofactor_setup_manual')}{' '}
                <span className={classes.secret}>{enrollment.secret}</span>
              </Typography>
            </>
          ) : (
            <Typography id="twofactor-modal-description" variant="body2" style={{ marginTop: 10 }}>
              {t('twofactor_info')}
            </Typography>
          )}
          <MUITextField
            style={{ marginTop: 20, marginBottom: 20, width: 250, display: 'block' }}
            variant="outlined"
            name="2fa"
            autoFocus
            autoComplete="one-time-code"
            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
            value={verificationCode}
            onChange={({ currentTarget: { value } }) => setVerificationCode(value.replace(/\D/g, ''))}
            onKeyUp={event => event.keyCode === 13 && verify2FA()}
            type="text"
            label={t('Verification code')} />
          <Button
            variant="contained"
            color="primary"
            style={{ backgroundColor: '#0df394', color: '#041e7a', marginRight: 20 }}
            size='large'
            disabled={verifying || verificationCode.length !== 6}
            onClick={verify2FA}
          >
            {t('verify')} &gt;
          </Button>
        </Box>
      </Modal>
    </>
  );
}
